<template>
  <form :class="formClasses" @submit.prevent="handleSubmit">
    <!-- 🧱 단순형 폼 -->
    <template v-if="schema.type === 'simple'">
      <div
        v-for="(field, index) in getOrderedFields(schema.fields || [])"
        :key="getFieldKey(field, index)"
        :class="fieldBaseClass(field)"
        :style="getFieldStyle(field)"
      >
        <JFormField
          :label="field.label"
          :required="getFieldRequired(field)"
          :orientation="getLabelPosition()"
          :label-align="getLabelAlign()"
          :label-width="getLabelWidth()"
          :type="(field.type === 'searchcombo' ? 'searchCombo' : field.type) as any"
          :input-type="field.inputType"
          :description="field.description"
          :inline-label="field.inlineLabel"
          :range-options="field.rangeOptions"
          v-bind="getFieldProps(field)"
        />
      </div>
    </template>

    <!-- 📦 섹션형 폼 -->
    <template v-else-if="schema.type === 'sectioned'">
      <div class="space-y-2">
        <JCard
          v-for="section in schema.sections"
          :key="section.id"
          :title="section.title"
          :collapsible="section.collapsible"
          :default-collapsed="section.defaultCollapsed"
        >
          <div :class="formClasses">
            <div
              v-for="(field, index) in getOrderedFields(section.fields || [])"
              :key="getFieldKey(field, index)"
              :class="fieldBaseClass(field)"
              :style="getFieldStyle(field)"
            >
              <JFormField
                :label="field.label"
                :required="getFieldRequired(field)"
                :orientation="getLabelPosition()"
                :label-align="getLabelAlign()"
                :label-width="getLabelWidth()"
                :type="(field.type === 'searchcombo' ? 'searchCombo' : field.type) as any"
                :input-type="field.inputType"
                :description="field.description"
                :inline-label="field.inlineLabel"
                v-bind="getFieldProps(field)"
              />
            </div>
          </div>
        </JCard>
      </div>
    </template>

    <!-- 🪜 스텝형 (Wizard) -->
    <template v-else-if="schema.type === 'wizard'">
      <div class="space-y-2">
        <div
          v-for="step in schema.steps"
          :key="step.id"
          class="p-3 border rounded-lg"
        >
          <h3 class="font-semibold text-lg">{{ step.title }}</h3>
          <p v-if="step.description" class="text-muted-foreground text-sm mb-2">
            {{ step.description }}
          </p>
          <div :class="formClasses">
            <div
              v-for="(field, index) in getOrderedFields(step.fields || [])"
              :key="getFieldKey(field, index)"
              :class="fieldBaseClass(field)"
              :style="getFieldStyle(field)"
            >
              <JFormField
                :label="field.label"
                :required="getFieldRequired(field)"
                :orientation="getLabelPosition()"
                :label-align="getLabelAlign()"
                :label-width="getLabelWidth()"
                :type="(field.type === 'searchcombo' ? 'searchCombo' : field.type) as any"
                :input-type="field.inputType"
                :description="field.description"
                :inline-label="field.inlineLabel"
                v-bind="getFieldProps(field)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

  </form>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { JFormField, JCard } from '@/components/molecules';
import type { FormSchema, DynamicFormField } from '@/types/dynamic-form';
import { resolveGlobalStyle } from '@/lib/styleTypePreset';

// props
const props = defineProps<{
  schema: FormSchema;
  modelValue?: Record<string, any>;
}>();

const emit = defineEmits(['update:modelValue', 'submit', 'change', 'error']);

const BOOLEAN_TYPES: Array<DynamicFormField['type']> = ['checkbox', 'switch'];

// 폼 상태 관리
const formState = reactive<Record<string, any>>({});

// 초기값 설정
watch(
  () => props.modelValue,
  (newValues) => {
    if (newValues) {
      Object.assign(formState, newValues);
    }
  },
  { immediate: true, deep: true }
);

// 필드 키 가져오기 (controlId > controlName > name > label 순서)
function getFieldKey(field: DynamicFormField, index: number): string {
  return field.controlId || field.controlName || (field as any).name || field.label || `field-${index}`;
}

// 필드 이름 가져오기 (controlName > name 순서)
function getFieldName(field: DynamicFormField): string {
  return field.controlName || (field as any).name || '';
}

// 필드 required 가져오기 (isRequired > required 순서)
function getFieldRequired(field: DynamicFormField): boolean {
  return field.isRequired !== undefined ? field.isRequired : ((field as any).required || false);
}

// 필드 초기화
watch(
  () => props.schema,
  (newSchema) => {
    if (!newSchema) return;
    const allFields: DynamicFormField[] = [];

    if (newSchema.type === 'simple' && newSchema.fields) {
      allFields.push(...newSchema.fields);
    } else if (newSchema.type === 'sectioned' && newSchema.sections) {
      newSchema.sections.forEach(section => {
        if (section.fields) allFields.push(...section.fields);
      });
    } else if (newSchema.type === 'wizard' && newSchema.steps) {
      newSchema.steps.forEach(step => {
        if (step.fields) allFields.push(...step.fields);
      });
    }
    
    allFields.forEach((field) => {
      const fieldName = getFieldName(field);
      if (fieldName && formState[fieldName] === undefined) {
        if (BOOLEAN_TYPES.includes(field.type)) {
          formState[fieldName] = 'N';
        } else if (field.type === 'radio') {
          formState[fieldName] = field.options?.[0]?.value ?? '';
        } else if (field.type === 'select' || field.type === 'searchcombo') {
          const addAll = field.addAll !== undefined ? field.addAll : ((field as any).allowAll || false);
          formState[fieldName] = addAll ? 'ALL' : 'SELECT';
        } else {
          formState[fieldName] = '';
        }
      }
    });
  },
  { immediate: true }
);

// 값 변경 감지 → 상위 emit
watch(formState, (val) => emit('update:modelValue', val), { deep: true });

// 이벤트 핸들러
function handleSubmit() {
  emit('submit', { ...formState });
  if (typeof props.schema.events?.onSubmit === 'function') {
    props.schema.events.onSubmit({ ...formState });
  }
}

function handleChange(field: string, value: any) {
  emit('change', { field, value });
  if (typeof props.schema.events?.onChange === 'function') {
    props.schema.events.onChange(field, value);
  }
}

function handleError(errs: any) {
  emit('error', errs);
  if (typeof props.schema.events?.onError === 'function') {
    props.schema.events.onError(errs);
  }
}

// 정렬된 필드 가져오기
function getOrderedFields(fields: DynamicFormField[]): DynamicFormField[] {
  const visibleFields = fields.filter((field) => field.isVisible !== false);
  return visibleFields.sort((a, b) => {
    // sortOrder 기준으로 정렬
    if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
    }
    const aName = a.controlName || (a as any).name || a.label || '';
    const bName = b.controlName || (b as any).name || b.label || '';
    return aName.localeCompare(bName);
  });
}

// 폼 클래스 계산
const formClasses = computed(() => {
  const colCount = props.schema.globalStyle?.colCount || 
    (props.schema.layout?.columns ? `row-${props.schema.layout.columns}` : 'row-1');
  const gapClass = getGapClass(colCount);
  
  // 반응형: 화면 크기에 따라 열 수 자동 조정
  let classes = '';
  switch (colCount) {
    case 'row-1':
      classes = `flex flex-col ${gapClass}`;
      break;
    case 'row':
    case 'row-2':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 auto-rows-min`;
      break;
    case 'row-3':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-3 auto-rows-min`;
      break;
    case 'row-4':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-4 auto-rows-min`;
      break;
    case 'row-5':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 auto-rows-min`;
      break;
    case 'row-6':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 auto-rows-min`;
      break;
    case 'row-7':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 auto-rows-min`;
      break;
    case 'row-8':
      classes = `flex flex-col md:grid md:grid-flow-row md:items-start ${gapClass} md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 auto-rows-min`;
      break;
    default:
      classes = `flex flex-col ${gapClass}`;
  }
  
  return classes;
});

// 필드 기본 클래스
function fieldBaseClass(field: DynamicFormField): string {
  return BOOLEAN_TYPES.includes(field.type)
    ? 'min-w-0 w-auto inline-flex items-center'
    : 'min-w-0 w-full';
}

// 최대 열 수 계산
function getMaxCols(colCount?: string): number {
  switch (colCount) {
    case 'row':
    case 'row-2': return 2;
    case 'row-3': return 3;
    case 'row-4': return 4;
    case 'row-5': return 5;
    case 'row-6': return 6;
    case 'row-7': return 7;
    case 'row-8': return 8;
    default: return 1;
  }
}

// 열 수에 따른 gap 계산
function getGapClass(colCount: string): string {
  switch (colCount) {
    case 'row-1': return 'gap-y-2';
    case 'row':
    case 'row-2': return 'gap-y-2 gap-x-3';
    case 'row-3': return 'gap-y-2 gap-x-3';
    case 'row-4': return 'gap-y-2 gap-x-3';
    case 'row-5': return 'gap-y-2 gap-x-3';
    case 'row-6': return 'gap-y-2 gap-x-3';
    case 'row-7': return 'gap-y-2 gap-x-3';
    case 'row-8': return 'gap-y-2 gap-x-3';
    default: return 'gap-y-2';
  }
}

// 열 수에 따른 라벨 너비 계산
function getLabelWidth(): string {
  const colCount = props.schema.globalStyle?.colCount || 
    (props.schema.layout?.columns ? `row-${props.schema.layout.columns}` : 'row-1');
  const maxCols = getMaxCols(colCount);
  if (maxCols >= 6) return '6rem';
  if (maxCols >= 5) return '7rem';
  return '8rem';
}

// 라벨 위치 가져오기
function getLabelPosition(): 'horizontal' | 'vertical' {
  return props.schema.globalStyle?.labelPosition || 'horizontal';
}

// 라벨 정렬 가져오기
function getLabelAlign(): 'left' | 'middle' | 'right' {
  return props.schema.globalStyle?.labelAlign || 'left';
}

// 필드 스타일 계산
function getFieldStyle(field: DynamicFormField): Record<string, string> {
  const colCount = props.schema.globalStyle?.colCount || 
    (props.schema.layout?.columns ? `row-${props.schema.layout.columns}` : 'row-1');
  const maxCols = getMaxCols(colCount);
  const span = Math.min(maxCols, Math.max(1, field.colSpan || 1));
  return {
    gridColumn: `span ${span}`
  };
}

// 필드 props 생성
function getFieldProps(field: DynamicFormField) {
  const fieldName = getFieldName(field);
  const disabled = field.isDisabled !== undefined ? field.isDisabled : ((field as any).disabled || false);
  const readonly = field.isReadonly !== undefined ? field.isReadonly : ((field as any).readonly || false);
  
  // baseClass 조정
  const baseClass = BOOLEAN_TYPES.includes(field.type)
    ? 'inline-flex items-center gap-2 w-auto'
    : 'w-full';
  
  // 전역 스타일 해결
  const resolvedStyle = resolveGlobalStyle(props.schema.globalStyle);
  
  // 열 수에 따라 컨트롤 크기 동적 조정
  const colCount = props.schema.globalStyle?.colCount || 
    (props.schema.layout?.columns ? `row-${props.schema.layout.columns}` : 'row-1');
  const maxCols = getMaxCols(colCount);
  let finalSize = resolvedStyle.controlSize;
  
  // 4열 이상일 때 컨트롤 크기를 작게 조정
  if (maxCols >= 6) {
    finalSize = 'sm';
  } else if (maxCols >= 5) {
    finalSize = 'sm';
  } else if (maxCols >= 4) {
    if (resolvedStyle.controlSize === 'lg') {
      finalSize = 'md';
    }
  }

  const createEventHandler = (eventType: string, value: any) => {
    if (fieldName) {
      formState[fieldName] = value;
      handleChange(fieldName, value);
    }
    
    if (field.events?.[eventType as keyof typeof field.events]) {
      const handlerName = field.events[eventType as keyof typeof field.events];
      console.log(`Event ${eventType}: Calling handler "${handlerName}" with value:`, value);
    }
  };

  if (BOOLEAN_TYPES.includes(field.type)) {
    return {
      modelValue: formState[fieldName] === 'Y' ? 'Y' : 'N',
      disabled,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        if (fieldName) {
          formState[fieldName] = value;
        }
        createEventHandler('onChange', value === 'Y');
      }
    };
  }

  if (field.type === 'radio') {
    return {
      modelValue: String(formState[fieldName] ?? ''),
      options: field.options ?? [],
      multiple: field.multiple,
      disabled,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        createEventHandler('onChange', value);
      }
    };
  }

  if (field.type === 'select' || field.type === 'combo') {
    return {
      modelValue: String(formState[fieldName] ?? ''),
      options: getSelectOptions(field),
      multiple: field.multiple,
      disabled,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        createEventHandler('onChange', value);
      }
    };
  }

  if (field.type === 'searchcombo') {
    return {
      modelValue: String(formState[fieldName] ?? ''),
      options: getSelectOptions(field),
      disabled,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        createEventHandler('onChange', value);
      }
    };
  }

  if (field.type === 'textarea') {
    return {
      modelValue: String(formState[fieldName] ?? ''),
      placeholder: field.placeholder ?? '',
      disabled,
      readonly,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        createEventHandler('onChange', value);
      }
    };
  }

  if (field.type === 'datepicker') {
    return {
      modelValue: String(formState[fieldName] ?? ''),
      placeholder: field.placeholder ?? '',
      disabled,
      readonly,
      styleType: finalSize,
      class: baseClass,
      'onUpdate:modelValue': (value: string) => {
        createEventHandler('onChange', value);
      }
    };
  }

  // 기본 input
  return {
    modelValue: String(formState[fieldName] ?? ''),
    inputType: field.inputType || (field.dataType === 'number' ? 'number' : 'text'),
    placeholder: field.placeholder ?? '',
    disabled,
    readonly,
    styleType: finalSize,
    class: baseClass,
    'onUpdate:modelValue': (value: string | number) => {
      createEventHandler('onChange', value);
    }
  };
}

// Select 옵션 생성
function getSelectOptions(field: DynamicFormField) {
  const addAll = field.addAll !== undefined ? field.addAll : ((field as any).allowAll || false);
  const defaultOption = {
    value: addAll ? 'ALL' : 'SELECT',
    label: addAll ? '전체' : '선택'
  };
  return [defaultOption, ...(field.options || [])];
}

// 폼 리셋
function reset() {
  Object.keys(formState).forEach(key => {
    delete formState[key];
  });
  
  // 모든 필드 수집
  const allFields: DynamicFormField[] = [];
  
  if (props.schema.type === 'simple' && props.schema.fields) {
    allFields.push(...props.schema.fields);
  } else if (props.schema.type === 'sectioned' && props.schema.sections) {
    props.schema.sections.forEach(section => {
      if (section.fields) allFields.push(...section.fields);
    });
  } else if (props.schema.type === 'wizard' && props.schema.steps) {
    props.schema.steps.forEach(step => {
      if (step.fields) allFields.push(...step.fields);
    });
  }
  
  // 초기값으로 재설정
  allFields.forEach((field) => {
    const fieldName = getFieldName(field);
    if (fieldName) {
      if (BOOLEAN_TYPES.includes(field.type)) {
        formState[fieldName] = 'N';
      } else if (field.type === 'radio') {
        formState[fieldName] = field.options?.[0]?.value ?? '';
      } else if (field.type === 'select' || field.type === 'searchcombo') {
        const addAll = field.addAll !== undefined ? field.addAll : ((field as any).allowAll || false);
        formState[fieldName] = addAll ? 'ALL' : 'SELECT';
      } else {
        formState[fieldName] = '';
      }
    }
  });
}

// 폼 상태 노출
defineExpose({
  formState,
  submit: handleSubmit,
  reset,
  handleError
});
</script>

<style scoped>
form {
  width: 100%;
}
</style>
